import { AccountsReceivableReportSource, BidPaymentStatus, DistributionBidPaymentType } from "api";
import type { SettlementRow } from "helpers/mutualSettlements";
import { body, request } from "services/request";

export interface PaymentOperation {
    id: number;
    amount: number;
    date: string;
    comment?: string | null;
    createdByName?: string;
}

export interface PaymentHistory {
    id: number;
    price?: number | null;
    remainingPayment?: number | null;
    receivedAmount?: number | null;
    paidPercent?: number | null;
    paymentStatus: BidPaymentStatus;
    planPaymentDate?: string | null;
    factPaymentDate?: string | null;
    invoiceDate?: string | null;
    overdueDays: number;
    comment?: string | null;
    operations: PaymentOperation[];
}

export interface PaymentDiscipline {
    completedPaymentsCount: number;
    overduePaymentsShare: number;
    averageOverdueDays: number;
    maxOverdueDays: number;
    currentOverdueDebt: number;
    currentOverdueBidsCount: number;
}

/** Select the correct payment side of a distribution order. */
function paymentIdentity(row: SettlementRow, payable: boolean) {
    return row.isDistributionBid
        ? {
              distributionBidId: row.bidId,
              paymentType: payable ? DistributionBidPaymentType.HiredCar : DistributionBidPaymentType.Customer,
          }
        : { bidId: row.bidId };
}

function resource(row: SettlementRow): string {
    return row.isDistributionBid ? "api/distributionBids" : "api/truckingBids";
}

/** Read history through the regular JSON middleware. */
export function getPaymentHistory(row: SettlementRow, payable: boolean): Promise<PaymentHistory | false> {
    const identity = paymentIdentity(row, payable);
    const suffix = row.isDistributionBid
        ? `?${new URLSearchParams(Object.entries(identity).map(([key, value]) => [key, String(value)]))}`
        : `/${row.bidId}`;
    return request(`${resource(row)}/getPaymentHistory${suffix}`, { method: "GET" });
}

/** Mutation endpoints return plain-string validation errors, which the operation editor displays inline. */
async function mutate(row: SettlementRow, payable: boolean, action: string, payload: Record<string, unknown>) {
    const result = await request(`${resource(row)}/${action}`, body({ ...paymentIdentity(row, payable), ...payload }), {
        withoutErrorAlert: true,
        returnErrorText: true,
        shouldAlert: true,
    });
    if (result?.errorText) {
        throw new Error(result.errorText);
    }
    if (!result) {
        throw new Error("Не удалось сохранить изменения. Повторите запрос.");
    }
    return result;
}

export function applyPaymentOperation(
    row: SettlementRow,
    payable: boolean,
    operation: Omit<PaymentOperation, "createdByName">,
) {
    return mutate(row, payable, "applyPaymentOperation", operation);
}

export function deletePaymentOperation(row: SettlementRow, payable: boolean, operationId: number) {
    return mutate(row, payable, "deletePaymentOperation", { operationId });
}

export function setPaymentCheckedStatus(row: SettlementRow, payable: boolean, isChecked: boolean) {
    return mutate(row, payable, "setPaymentCheckedStatus", { isChecked });
}

export function getPaymentDiscipline(params: {
    counterpartyId: number;
    source: AccountsReceivableReportSource;
    from: string;
    to: string;
}): Promise<PaymentDiscipline | false> {
    const query = new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)]));
    return request(`api/cargoOwnerDictionary/getPaymentDiscipline?${query}`, { method: "GET" });
}
